import { useCallback, useState } from 'react';

/** Local-only thumbs feedback for an assistant message (no persistence/API). */
export type MessageFeedbackSelection = 'up' | 'down' | null;

export interface MessageFeedbackSelectionState {
  isThumbUpSelected: boolean;
  isThumbDownSelected: boolean;
  handleThumbUpClick: () => void;
  handleThumbDownClick: () => void;
}

/**
 * Mutually-exclusive thumbs up/down selection. Clicking the currently selected
 * option clears it. State is local UI only — nothing is sent anywhere.
 */
export function useMessageFeedbackSelection(): MessageFeedbackSelectionState {
  const [feedbackSelection, setFeedbackSelection] = useState<MessageFeedbackSelection>(null);

  const toggle = useCallback((next: 'up' | 'down') => {
    setFeedbackSelection((prev) => (prev === next ? null : next));
  }, []);

  const handleThumbUpClick = useCallback(() => toggle('up'), [toggle]);
  const handleThumbDownClick = useCallback(() => toggle('down'), [toggle]);

  return {
    isThumbUpSelected: feedbackSelection === 'up',
    isThumbDownSelected: feedbackSelection === 'down',
    handleThumbUpClick,
    handleThumbDownClick,
  };
}
